// BUILD-046 task 8.6: the Done model (design "Done"; Req 8.1 - 8.3, 8.11, 8.13).
//
// Done is a per-item state machine:  idle -> saving -> idle | failed  (and failed -> saving on another activation).
//  - The activation changes the state SYNCHRONOUSLY (`activateDone` is a pure function the click handler calls before it awaits anything),
//    so the saving state shows well inside 200 ms and the control is disabled at once (Req 8.2).
//  - A second activation while saving sends nothing (`send: false`).
//  - A failed request (an error answer, or no answer in 10 s) leaves the task open in the status it held: nothing here ever writes a new
//    status, the row keeps showing the server's status, and the message says the task was not completed (Req 8.3, 8.11).
//  - After a success the row offers an inline Undo that calls the existing restore route (Undo is its own small machine, below).
//
// Who sees Done: only the CURRENT ASSIGNEE of an ordinary open task, on a row of their own list (`doneControl`). The UI never offers it
// to the requester, a collaborator, a manager, an administrator, a group administrator or the Owner acting only in that capacity, and an
// admin or PM role is not even an input. This only reflects the rule; the server decides (Req 8.13), and a refusal is shown as such.
//
// Pure module: no React, no I/O, no runtime imports. Erasable TypeScript only (run by `node --experimental-strip-types`).

/** Req 8.3: no answer within 10 seconds is a failure. The client (api/workShared.ts) enforces the same limit; a test ties them together. */
export const DONE_TIMEOUT_MS = 10000;

export type DonePhase = 'idle' | 'saving' | 'failed';
export type UndoPhase = 'none' | 'available' | 'saving' | 'failed';

export interface DoneFailure {
  /** Always says the task was not completed (Req 8.3). */
  headline: string;
  /** The server's message or the reason there was no answer; null when there is nothing more to say. */
  detail: string | null;
}

export interface DoneItemState {
  phase: DonePhase;
  /** The status the task held when Done was activated; shown while saving and after a failure. */
  priorStatus: string | null;
  failure: DoneFailure | null;
  undo: UndoPhase;
  undoFailure: DoneFailure | null;
}

export interface DoneState {
  readonly items: Readonly<Record<string, DoneItemState>>;
}

const OPEN_STATUSES: readonly string[] = ['active', 'in_progress', 'waiting', 'blocked'];

const IDLE: DoneItemState = Object.freeze({ phase: 'idle', priorStatus: null, failure: null, undo: 'none', undoFailure: null }) as DoneItemState;

export function initialDoneState(): DoneState {
  return { items: {} };
}

/** The item's state; an item that was never activated is idle. */
export function itemDone(state: DoneState, id: string): DoneItemState {
  return state.items[id] ?? IDLE;
}

function put(state: DoneState, id: string, next: DoneItemState): DoneState {
  return { items: { ...state.items, [id]: next } };
}

// ───────────────────────── Done ─────────────────────────

/**
 * The click handler. `send` is true exactly when the caller must now send the Done request. A task that is not open (already completed,
 * unknown status) sends nothing, and so does an activation while a request for the task is in progress.
 */
export function activateDone(state: DoneState, id: string, currentStatus: string): { state: DoneState; send: boolean } {
  const current = itemDone(state, id);
  if (current.phase === 'saving') return { state, send: false };
  if (!OPEN_STATUSES.includes(currentStatus)) return { state, send: false };
  return { state: put(state, id, { ...current, phase: 'saving', priorStatus: currentStatus, failure: null, undo: 'none', undoFailure: null }), send: true };
}

/** The server accepted Done. The caller merges the server's item into the list. Applies only to a request in progress. */
export function doneSucceeded(state: DoneState, id: string): DoneState {
  const current = itemDone(state, id);
  if (current.phase !== 'saving') return state;
  return put(state, id, { phase: 'idle', priorStatus: null, failure: null, undo: 'available', undoFailure: null });
}

/** The request failed or timed out: back to an open task in its prior status, with the not-completed message. */
export function doneFailed(state: DoneState, id: string, thrown: unknown): DoneState {
  const current = itemDone(state, id);
  if (current.phase !== 'saving') return state;
  return put(state, id, { ...current, phase: 'failed', failure: describeDoneFailure(thrown) });
}

/**
 * A request in progress ended and the row has nothing to retry or undo (Done with a note: its own panel says what happened, doneNoteModel.ts). Back to
 * idle with no Undo offer. Applies only to a request in progress.
 */
export function doneSettled(state: DoneState, id: string): DoneState {
  const current = itemDone(state, id);
  if (current.phase !== 'saving') return state;
  return put(state, id, { phase: 'idle', priorStatus: null, failure: null, undo: 'none', undoFailure: null });
}

// ───────────────────────── Undo ─────────────────────────

/** The Undo click: true to send the restore request. Only offered right after a success, and only once at a time. */
export function activateUndo(state: DoneState, id: string): { state: DoneState; send: boolean } {
  const current = itemDone(state, id);
  if (current.undo !== 'available' && current.undo !== 'failed') return { state, send: false };
  return { state: put(state, id, { ...current, undo: 'saving', undoFailure: null }), send: true };
}

export function undoSucceeded(state: DoneState, id: string): DoneState {
  const current = itemDone(state, id);
  if (current.undo !== 'saving') return state;
  return put(state, id, { phase: 'idle', priorStatus: null, failure: null, undo: 'none', undoFailure: null });
}

export function undoFailed(state: DoneState, id: string, thrown: unknown): DoneState {
  const current = itemDone(state, id);
  if (current.undo !== 'saving') return state;
  return put(state, id, { ...current, undo: 'failed', undoFailure: describeUndoFailure(thrown) });
}

/** The Undo offer goes away on its own when the user dismisses it or moves on. */
export function dismissUndo(state: DoneState, id: string): DoneState {
  const current = itemDone(state, id);
  if (current.undo === 'none' || current.undo === 'saving') return state;
  return put(state, id, { ...current, undo: 'none', undoFailure: null });
}

/** A Caller change: nothing that belongs to the previous Caller's session (an Undo offer, a saving state) survives. */
export function resetDoneState(): DoneState {
  return initialDoneState();
}

// ───────────────────────── messages ─────────────────────────

export const NOT_COMPLETED = 'This task was not completed';
export const NOT_REOPENED = 'This task was not reopened';

function failureDetail(thrown: unknown, limitMs: number): string | null {
  if (thrown !== null && typeof thrown === 'object') {
    const t = thrown as { kind?: unknown; message?: unknown };
    if (t.kind === 'timeout') return `There was no answer within ${Math.round(limitMs / 1000)} seconds. The task is still open.`;
    if (t.kind === 'network') return 'The server could not be reached. The task is still open.';
    if (typeof t.message === 'string' && t.message.trim() !== '') return t.message;
  }
  return null;
}

export function describeDoneFailure(thrown: unknown): DoneFailure {
  return { headline: NOT_COMPLETED, detail: failureDetail(thrown, DONE_TIMEOUT_MS) };
}

export function describeUndoFailure(thrown: unknown): DoneFailure {
  return { headline: NOT_REOPENED, detail: failureDetail(thrown, DONE_TIMEOUT_MS) };
}

// ───────────────────────── who is offered Done ─────────────────────────

export type DoneListView = 'my-tasks' | 'shared' | 'oversight' | 'team';

export interface DoneRow {
  status: string;
  ownerEmployeeId?: string;
  sourceGoverned?: boolean;
  redacted?: boolean;
  lifecycle?: string;
  unclaimed?: boolean;
}

export interface DoneViewer {
  employeeId: string;
}

/**
 * Whether the row carries a Done control. `done` only for the current assignee of an ordinary open task on a row the viewer owns.
 *  - A row owned by someone else (the oversight list, a requester's list, a manager's view) never offers it.
 *  - A legacy row that carries no canonical owner is offered Done only in `my-tasks`, whose rows the server selected as the viewer's own.
 *  - A completed, cancelled, restricted or Source_Governed task has no Done control (a Source_Governed task shows none in S1; its review
 *    control arrives in S2).
 * A role (owner, admin, pm) is not an input, so it can never add a Done control to someone else's task.
 */
export function doneControl(row: DoneRow, viewer: DoneViewer | null, view: DoneListView): 'done' | 'none' {
  if (!viewer || typeof viewer.employeeId !== 'string' || viewer.employeeId === '') return 'none';
  if (!OPEN_STATUSES.includes(row.status)) return 'none';
  if (row.sourceGoverned === true || row.redacted === true || row.unclaimed === true) return 'none';
  if (row.lifecycle === 'cancelled') return 'none';
  if (typeof row.ownerEmployeeId === 'string' && row.ownerEmployeeId !== '') return row.ownerEmployeeId === viewer.employeeId ? 'done' : 'none';
  return view === 'my-tasks' ? 'done' : 'none';
}

// ───────────────────────── what the row shows ─────────────────────────

export interface DoneControlView {
  /** Whether to render a Done control at all. */
  visible: boolean;
  /** Disabled while saving. */
  disabled: boolean;
  label: 'Done' | 'Saving…';
  /** The status the row shows: the prior status while saving or after a failure, otherwise the server's. */
  displayStatus: string;
  failure: DoneFailure | null;
  undo: { visible: boolean; disabled: boolean; label: 'Undo' | 'Undoing…'; failure: DoneFailure | null };
}

export function describeDoneControl(state: DoneState, id: string, row: DoneRow, viewer: DoneViewer | null, view: DoneListView): DoneControlView {
  const item = itemDone(state, id);
  const offered = doneControl(row, viewer, view) === 'done';
  const holding = item.phase === 'saving' || item.phase === 'failed';
  return {
    visible: offered || item.phase === 'saving',
    disabled: item.phase === 'saving',
    label: item.phase === 'saving' ? 'Saving…' : 'Done',
    displayStatus: holding && item.priorStatus !== null ? item.priorStatus : row.status,
    failure: item.phase === 'failed' ? item.failure : null,
    undo: {
      visible: item.undo !== 'none',
      disabled: item.undo === 'saving',
      label: item.undo === 'saving' ? 'Undoing…' : 'Undo',
      failure: item.undo === 'failed' ? item.undoFailure : null,
    },
  };
}
